import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ForwardedRef, ReactNode } from "react";
import { classNames } from "../utils/classNames";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "link";

export type ButtonSize = "sm" | "md" | "lg";

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  isLoading?: boolean;
  loadingLabel?: string;
};

function ButtonComponent(
  {
    children,
    className,
    disabled = false,
    isLoading = false,
    leadingIcon,
    loadingLabel = "正在处理",
    size = "md",
    trailingIcon,
    type = "button",
    variant = "secondary",
    ...props
  }: ButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const isDisabled = disabled || isLoading;

  return (
    <button
      {...props}
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={isLoading || undefined}
      className={classNames(
        "herskin-button",
        `herskin-button--${variant}`,
        `herskin-button--${size}`,
        isLoading && "herskin-button--loading",
        className,
      )}
    >
      <span
        className={classNames(
          "herskin-button__content",
          isLoading && "herskin-button__content--hidden",
        )}
      >
        {leadingIcon ? (
          <span className="herskin-button__icon" aria-hidden="true">
            {leadingIcon}
          </span>
        ) : null}
        <span>{children}</span>
        {trailingIcon ? (
          <span className="herskin-button__icon" aria-hidden="true">
            {trailingIcon}
          </span>
        ) : null}
      </span>

      {isLoading ? (
        <span className="herskin-button__loading">
          <span className="herskin-spinner" aria-hidden="true" />
          <span className="herskin-sr-only">{loadingLabel}</span>
        </span>
      ) : null}
    </button>
  );
}

export const Button = forwardRef(ButtonComponent);
Button.displayName = "Button";
